'use client';

import { useState, useEffect } from 'react';
import { api } from '../../core/apiClient';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Edit2, Trash2, Plus, ChevronDown, ChevronRight } from 'lucide-react';

interface Account {
  id: number;
  code: string;
  name: string;
  type: string;
  status: string;
  balance_debit: number;
  balance_credit: number;
  children?: Account[];
}

export default function ChartOfAccounts() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    type: 'Actiu',
    parent_id: '',
    status: 'Activa',
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const response: any = await api.get('/comptabilitat/chart-of-accounts/tree');
      if (response.success) {
        setAccounts(response.data || []);
      }
    } catch (error) {
      console.error('Error carregant comptes:', error);
    }
  };

  const handleToggleExpand = (id: number) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const handleAddAccount = async () => {
    if (!formData.code || !formData.name) {
      alert('Cal omplir tots els camps obligatoris');
      return;
    }

    try {
      const payload = {
        code: formData.code,
        name: formData.name,
        type: formData.type,
        status: formData.status,
        ...(formData.parent_id && { parent_id: parseInt(formData.parent_id) }),
      };

      if (editingId) {
        await api.put(`/comptabilitat/chart-of-accounts/${editingId}`, payload);
      } else {
        await api.post('/comptabilitat/chart-of-accounts', payload);
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({ code: '', name: '', type: 'Actiu', parent_id: '', status: 'Activa' });
      loadAccounts();
      alert('Compte desat correctament');
    } catch (error: any) {
      alert('Error: ' + (error.message || 'Error desconegut'));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Estàs segur que vols eliminar aquest compte?')) {
      return;
    }

    try {
      await api.delete(`/comptabilitat/chart-of-accounts/${id}`);
      loadAccounts();
      alert('Compte eliminat correctament');
    } catch (error: any) {
      alert('Error: ' + error.message);
    }
  };

  const handleEdit = (account: Account) => {
    setEditingId(account.id);
    setFormData({
      code: account.code,
      name: account.name,
      type: account.type,
      parent_id: '',
      status: account.status,
    });
    setShowForm(true);
  };

  const renderAccountTree = (accounts: Account[], level: number = 0) => {
    return (
      <>
        {accounts.map((account) => (
          <div key={account.id}>
            <div
              className="flex items-center gap-2 p-3 hover:bg-gray-50 border-b"
              style={{ paddingLeft: `${level * 24 + 12}px` }}
            >
              {account.children && account.children.length > 0 && (
                <button
                  onClick={() => handleToggleExpand(account.id)}
                  className="p-0 hover:bg-gray-200 rounded"
                >
                  {expandedIds.has(account.id) ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
              )}
              {(!account.children || account.children.length === 0) && (
                <div className="w-4" />
              )}

              <div className="flex-1">
                <div className="font-medium text-sm">
                  {account.code} - {account.name}
                </div>
                <div className="text-xs text-gray-500">
                  {account.type} | {account.status}
                </div>
              </div>

              <div className="text-right mr-4">
                <div className="text-sm font-mono">
                  D: {account.balance_debit.toFixed(2)}
                </div>
                <div className="text-sm font-mono">
                  H: {account.balance_credit.toFixed(2)}
                </div>
              </div>

              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleEdit(account)}
                  title="Editar"
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(account.id)}
                  title="Eliminar"
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {expandedIds.has(account.id) &&
              account.children &&
              account.children.length > 0 &&
              renderAccountTree(account.children, level + 1)}
          </div>
        ))}
      </>
    );
  };

  return (
    <div className="flex flex-col gap-6 p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Pla de Comptes</h1>
        <Button onClick={() => setShowForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Nou Compte
        </Button>
      </div>

      {showForm && (
        <Card className="p-6">
          <h2 className="text-sm font-bold mb-4">
            {editingId ? 'Editar Compte' : 'Crear Compte'}
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Codi</label>
              <Input
                value={formData.code}
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value })
                }
                placeholder="1000"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Nom</label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Actius Corrents"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Tipus</label>
              <select
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                <option>Actiu</option>
                <option>Passiu</option>
                <option>Patrimoni</option>
                <option>Ingrés</option>
                <option>Despesa</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Estat</label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                <option>Activa</option>
                <option>Inactiva</option>
                <option>Tancada</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2 mt-6">
            <Button
              onClick={handleAddAccount}
              className="bg-green-600 hover:bg-green-700"
            >
              Desar
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
                setFormData({
                  code: '',
                  name: '',
                  type: 'Actiu',
                  parent_id: '',
                  status: 'Activa',
                });
              }}
            >
              Cancel·lar
            </Button>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="p-4 border-b flex gap-4 text-[12px] font-medium text-muted-foreground">
          <div className="flex-1">Compte</div>
          <div className="w-32 text-right">Saldos</div>
          <div className="w-32">Accions</div>
        </div>
        {accounts.length > 0 ? (
          renderAccountTree(accounts)
        ) : (
          <div className="p-6 text-center text-gray-500">
            No hi ha comptes registrats
          </div>
        )}
      </Card>
    </div>
  );
}
